import { Pressable, View } from 'react-native';
import { router } from 'expo-router';
import { CalendarClock, ClipboardList, FileText, Heart, Link2, Settings, ShoppingBasket, UserRound } from 'lucide-react-native';
import { colors, formatDayInSentence, formatLongDate, formatMinutes, parseDate, summarize, tasksInPlan } from '@hogar/shared';
import { Card, RowLink, Screen, Txt, useT } from '../../components/ui';
import { ProgressRing } from '../../components/ProgressRing';
import { toRestock, useHouse } from '../../lib/house';
import { crewSlice, hasGarden } from '../../lib/crew';
import type { ApiTask } from '@hogar/shared';

export default function Home() {
  const { t, lang } = useT();
  const snap = useHouse();
  const { owner, products, staff, todayDate } = snap;
  const restock = toRestock(products).length;
  // Once there is a garden, the house and the garden each get their own day card.
  const garden = hasGarden(snap);
  const house = crewSlice(snap, 'house');
  const gardenSide = crewSlice(snap, 'garden');

  return (
    <Screen>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
        <View style={{ flex: 1 }}>
          <Txt weight="semibold" size={26}>
            {t('hello', { name: owner.name })}
          </Txt>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <Txt color={colors.textMuted} size={15}>
              {t('allGood')}
            </Txt>
            <Heart size={14} color={colors.textMuted} />
          </View>
        </View>
        <Pressable onPress={() => router.navigate('/mas')} hitSlop={12} style={{ padding: 6, borderRadius: 20, borderWidth: 1.5, borderColor: colors.text }}>
          <UserRound size={18} color={colors.text} />
        </Pressable>
      </View>

      <Txt weight="semibold" size={15} style={{ marginTop: 18, marginBottom: 10 }}>
        {formatLongDate(todayDate, lang)}
      </Txt>

      <DayCard title={t('todaysCleaning')} tasks={house.tasks} />
      {garden && <DayCard title={t('gardenToday')} tasks={gardenSide.tasks} garden />}

      {staff.length > 0 && (
        <Card>
          {staff.map((p, i) => (
            <RowLink key={p.id} Icon={Link2} title={t('sendLinkTo', { name: p.firstName })} onPress={() => router.navigate('/mas')} last={i === staff.length - 1} />
          ))}
        </Card>
      )}

      <Card>
        <RowLink Icon={ClipboardList} title={t('routines')} subtitle={t('routinesSub')} onPress={() => router.navigate('/rutinas')} />
        <RowLink Icon={ShoppingBasket} title={t('products')} subtitle={t('productsSub')} badge={restock} onPress={() => router.navigate('/productos')} />
        <RowLink Icon={FileText} title={t('reports')} subtitle={t('reportsSub')} onPress={() => router.navigate('/reportes')} />
        <RowLink Icon={Settings} title={t('settings')} subtitle={t('settingsSub')} onPress={() => router.navigate('/mas')} last />
      </Card>
    </Screen>
  );
}

/** One crew's day: estimated time and how many tasks are done. */
function DayCard({ title, tasks, garden }: { title: string; tasks: ApiTask[]; garden?: boolean }) {
  const { t, lang } = useT();
  const { today, plans, moves } = useHouse();
  const crew = garden ? 'garden' : 'house';
  const todays = tasksInPlan(plans[today.date]?.[crew], today.weekday, tasks);
  const s = summarize(todays, today.results);
  // One-time day moves that touch today (Mike's wife, 2026-10-22).
  const away = moves.find((m) => m.crew === crew && m.fromDate === today.date);
  const here = moves.find((m) => m.crew === crew && m.toDate === today.date);
  const fmt = (iso: string) => formatDayInSentence(parseDate(iso), lang);
  return (
    <Card style={{ paddingVertical: 16 }}>
      <Txt weight="medium" size={16}>
        {title}
      </Txt>
      {away && (
        <Txt size={14} color={colors.textMuted} style={{ marginTop: 6 }}>
          {t('movedAway', { to: fmt(away.toDate) })}
        </Txt>
      )}
      {here && (
        <Txt size={14} color={colors.textMuted} style={{ marginTop: 6 }}>
          {t('movedHere', { from: fmt(here.fromDate) })}
        </Txt>
      )}
      {s.total === 0 ? (
        away ? null : (
          <Txt color={colors.textMuted} style={{ marginTop: 6 }}>
            {t(garden ? 'dayOffGarden' : 'dayOff')}
          </Txt>
        )
      ) : (
        <>
          <Txt size={14} color={colors.textMuted}>
            <Txt weight="semibold" size={14}>
              {formatMinutes(s.minutes)}
            </Txt>{' '}
            · {t('estimated')}
          </Txt>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, marginTop: 14 }}>
            <ProgressRing done={s.done} total={s.total} />
            <View>
              <Txt weight="semibold" size={20}>
                {s.done}/{s.total}
              </Txt>
              <Txt size={14} color={colors.textMuted}>
                {t('tasksCompleted')}
              </Txt>
            </View>
          </View>
        </>
      )}
      <Pressable onPress={() => router.push({ pathname: '/move-day', params: { crew } })} hitSlop={8} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 14 }} accessibilityRole="button">
        <CalendarClock size={18} color={colors.primary} />
        <Txt weight="medium" size={15} color={colors.primary}>
          {t('moveDay')}
        </Txt>
      </Pressable>
    </Card>
  );
}
